import axios, { type AxiosError, type AxiosResponse } from 'axios'
import isEqual from 'lodash.isequal'
import { useCallback, useMemo } from 'react'
import useSWR, {
  type MutatorCallback,
  type MutatorOptions,
  type SWRConfiguration,
  type SWRResponse,
  unstable_serialize,
  useSWRConfig,
} from 'swr'
import { useAuthContext } from '@/providers/Global'

//  ///////////////////////
//  MARK: TYPES
// `null` est l'idiome SWR de la récupération conditionnelle — « ne va rien chercher tant que la
// condition n'est pas remplie ». parseSWRKeys le traite déjà comme une absence de clé ; seul le
// type l'ignorait, ce qui obligeait chaque appelant à mentir sur son intention.
type TKey = string | [string, ...unknown[]] | false | null | undefined

export interface IHUseSWRAxiosResult<T> extends SWRResponse<AxiosResponse<T>, AxiosError> {
  type: 'Normal'
  isLoading: boolean
}

//  ///////////////////////
//  MARK: FUNCTIONS
export const parseSWRKeys = (keys: TKey, id: string): [string, ...unknown[]] | false => {
  if (!keys || keys.length === 0) {
    return false
  }

  return Array.isArray(keys) ? [...keys, id] : [keys, id]
}

export const resolveSWRKey = (
  key: (() => TKey) | TKey,
  username: string | undefined,
): [string, ...unknown[]] | false =>
  parseSWRKeys(typeof key === 'function' ? key() : key, username ?? 'NOT LOGGED IN')

// Deux AxiosResponse ne sont jamais égales pour SWR (en-tête `date`, AxiosHeaders) : sans ce
// compare, chaque rafraîchissement de 60 s change l'identité des données, et tout effet qui en
// dépend réécrit la saisie en cours. On compare donc ce que l'appelant lit : statut et corps.
export const compareAxiosResponses = <T>(
  a: AxiosResponse<T> | undefined,
  b: AxiosResponse<T> | undefined,
): boolean =>
  a === b ||
  (a !== undefined && b !== undefined && a.status === b.status && isEqual(a.data, b.data))

//  ///////////////////////
//  MARK: EXPORTS
// Remplit le cache sous la clé de useSWRAxios : le montage affiche aussitôt puis revalide (un
// `preload` SWR, lui, servirait plus tard une réponse ancienne comme fraîche). Clé déjà en cache : rien.
export function usePreloadSWRAxios() {
  const Auth = useAuthContext()
  const { cache, mutate } = useSWRConfig()
  const username = Auth.active?.username ?? 'NOT LOGGED IN'

  return useCallback(
    (url: string) => {
      const key = parseSWRKeys(url, username)

      if (key && cache.get(unstable_serialize(key))?.data === undefined) {
        mutate(key, axios.get(url), { revalidate: false }).catch(() => undefined)
      }
    },
    [cache, mutate, username],
  )
}

export function useSWRAxios<T>(
  key: (() => TKey) | TKey,
  config?: SWRConfiguration<AxiosResponse<T>>,
  block?: boolean,
): IHUseSWRAxiosResult<T> {
  const Auth = useAuthContext()

  const swrConfig = useMemo<SWRConfiguration<AxiosResponse<T>>>(() => {
    const value: SWRConfiguration<AxiosResponse<T>> = { compare: compareAxiosResponses, ...config }

    if (block) {
      value.refreshInterval = 0
      value.refreshWhenHidden = false
      value.refreshWhenOffline = false
      value.revalidateOnReconnect = false
      value.revalidateOnFocus = false
    }

    return value
  }, [block, config])

  const swrKey = useMemo(() => resolveSWRKey(key, Auth.active?.username), [Auth.active, key])

  const hook = useSWR<AxiosResponse<T>, AxiosError>(
    swrKey,
    (args: unknown[]) => axios.get<T>(args[0] as string),
    swrConfig,
  )

  return useMemo(
    () => ({
      type: 'Normal',
      ...hook,
      isLoading: !hook.data && !hook.error,
    }),
    [hook],
  )
}

export function useSWRAxiosMutate<T>(): (
  key: (() => TKey) | TKey,
  data?: T | Promise<T> | MutatorCallback<T>,
  opts?: boolean | MutatorOptions<T>,
) => Promise<T | undefined> {
  const Auth = useAuthContext()

  const config = useSWRConfig()

  return useCallback(
    (key, ...args) => config.mutate<T>(resolveSWRKey(key, Auth.active?.username), ...args),
    [Auth.active, config],
  )
}
